import type { User } from '@compass/client/types/user';
import { ChevronsUpDown, Loader2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserCell } from '@/components/UserCell';
import { useOrganizationUsers } from '@/hooks/useOrganizationUsers';
import { useProgramRoles } from '@/hooks/useProgramRoles';
import { useProgramUsers } from '@/hooks/useProgramUsers';
import { useAddProgramUserRole } from '@/hooks/useUserRoles';

interface AddProgramMembersDialogProps {
  organizationId: string;
  programId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddProgramMembersDialog: React.FC<AddProgramMembersDialogProps> = ({
  organizationId,
  programId,
  open,
  onOpenChange,
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const { data: orgUsersData, isLoading: isLoadingOrgUsers } = useOrganizationUsers(
    organizationId,
    { limit: 100 },
  );

  const { data: programUsersData, isLoading: isLoadingProgramUsers } = useProgramUsers(
    organizationId,
    programId,
    { limit: 100 },
  );

  const { data: rolesData, isLoading: isLoadingRoles } = useProgramRoles();

  const addRoleMutation = useAddProgramUserRole();

  // Create a set of existing program member IDs for fast lookup
  const existingMemberIds = useMemo(() => {
    return new Set(programUsersData?.users?.map(u => u.user_id) || []);
  }, [programUsersData]);

  // Filter out users who are already members
  const orgUsers = useMemo(() => {
    return (orgUsersData?.users || []).filter(user => !existingMemberIds.has(user.user_id || ''));
  }, [orgUsersData, existingMemberIds]);

  // Selected users for display
  const selectedUsers = useMemo(() => {
    return orgUsers.filter(u => selectedUserIds.includes(u.user_id || ''));
  }, [orgUsers, selectedUserIds]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedUserIds([]);
      setSelectedRoleId('');
      setIsSubmitting(false);
    }
  }, [open]);

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId],
    );
  };

  const removeUser = (userId: string) => {
    setSelectedUserIds(prev => prev.filter(id => id !== userId));
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0 || !selectedRoleId) return;

    setIsSubmitting(true);
    let successCount = 0;
    const errors: Array<{ user: User; error: string }> = [];

    for (const userId of selectedUserIds) {
      try {
        await addRoleMutation.mutateAsync({
          organization_id: organizationId,
          program_id: programId,
          user_id: userId,
          role_id: selectedRoleId,
        });
        successCount++;
      } catch (error: any) {
        const user = orgUsers.find(u => u.user_id === userId);
        errors.push({
          user: user as User,
          error: error?.response?.data?.message || error?.message || 'Unknown error',
        });
      }
    }

    setIsSubmitting(false);

    // Display results
    if (errors.length === 0) {
      toast.success(
        `Successfully added ${successCount} ${successCount === 1 ? 'member' : 'members'}`,
      );
      onOpenChange(false);
    } else if (successCount > 0) {
      toast.warning(
        `Added ${successCount} ${successCount === 1 ? 'member' : 'members'}. ${errors.length} failed.`,
      );
      onOpenChange(false);
    } else {
      toast.error(`Failed to add members: ${errors[0]?.error || 'Unknown error'}`);
    }
  };

  const availableRoles = rolesData?.roles || [];
  const isLoading = isLoadingOrgUsers || isLoadingProgramUsers || isLoadingRoles;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Members to Program</DialogTitle>
          <DialogDescription>
            Select members from your organization and assign them a role
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* User Selection */}
            <div>
              <Label>Select Members</Label>
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboboxOpen}
                    className="mt-2 w-full justify-between"
                  >
                    {selectedUserIds.length > 0
                      ? `${selectedUserIds.length} ${selectedUserIds.length === 1 ? 'member' : 'members'} selected`
                      : 'Select members...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[550px] p-0">
                  <Command>
                    <CommandInput placeholder="Search users..." />
                    <CommandEmpty>No users found.</CommandEmpty>
                    <CommandList
                      className="max-h-[300px] overflow-y-auto"
                      onWheel={e => e.stopPropagation()}
                    >
                      <CommandGroup>
                        {orgUsers.map(user => {
                          const isSelected = selectedUserIds.includes(user.user_id || '');

                          return (
                            <CommandItem
                              key={user.user_id}
                              value={`${user.name} ${user.email}`}
                              onSelect={() => toggleUserSelection(user.user_id || '')}
                            >
                              <Checkbox checked={isSelected} className="mr-2" />
                              <UserCell user={user} size="md" />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Selected users display */}
              {selectedUsers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedUsers.map(user => (
                    <Badge key={user.user_id} variant="secondary" className="gap-1">
                      {user.name || user.email}
                      <button
                        onClick={() => removeUser(user.user_id || '')}
                        className="ml-1 rounded-full hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Role Selection */}
            <div>
              <Label>Assign Role</Label>
              <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map(role => (
                    <SelectItem key={role.id} value={role.id || ''}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-sm text-muted-foreground">
                This role will be assigned to all selected members
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedUserIds.length === 0 || !selectedRoleId || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add {selectedUserIds.length} {selectedUserIds.length === 1 ? 'Member' : 'Members'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
